/**
 * ScreenDescription Component
 * Subtext/description component with consistent typography
 */

import React, { type ReactNode } from 'react';
import { designTokens } from '../../constants/designTokens';

interface ScreenDescriptionProps {
  children: ReactNode;
  size?: 'sm' | 'base' | 'lg' | 'xl';
  color?: 'primary' | 'secondary';
  desktopEnhanced?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

export const ScreenDescription = React.memo(function ScreenDescription({
  children,
  size = 'base',
  color = 'secondary',
  desktopEnhanced = true,
  className = '',
  style,
}: ScreenDescriptionProps) {
  return (
    <>
      <p
        className={`screen-description screen-description-${size} screen-description-${color} ${desktopEnhanced ? 'desktop-enhanced' : ''} ${className}`}
        style={style}
      >
        {children}
      </p>

      <style>{`
        .screen-description {
          font-family: ${designTokens.typography.fontFamily.primary};
          font-weight: ${designTokens.typography.fontWeight.normal};
          line-height: ${designTokens.typography.lineHeight.relaxed};
          margin: 0;
          margin-bottom: ${designTokens.spacing.sm}; /* Standard 8px spacing */
          text-align: center;
          margin-left: auto;
          margin-right: auto;
        }
        
        /* Mobile-First Size Variants - Industry Standard */
        .screen-description-sm {
          font-size: ${designTokens.typography.fontSize.sm};   /* 14px */
          max-width: 90%;
        }
        
        .screen-description-base {
          font-size: ${designTokens.typography.fontSize.base}; /* 16px - Default */
          max-width: 90%;
        }
        
        .screen-description-lg {
          font-size: ${designTokens.typography.fontSize.lg};   /* 18px */
          max-width: 95%;
        }
        
        .screen-description-xl {
          font-size: ${designTokens.typography.fontSize.xl};   /* 20px */
          max-width: 95%;
        }
        
        /* Color variants */
        .screen-description-primary {
          color: ${designTokens.colors.text.primary};
        }
        
        .screen-description-secondary {
          color: ${designTokens.colors.text.secondary};
        }
        
        /* Mobile - Uses base sizes defined above */
        ${designTokens.media.mobile} {
          /* Mobile uses 14-20px range as defined in base styles */
          .screen-description {
            max-width: 95%;
          }
        }
        
        /* Tablet (768px-1023px) - Slight scale up */
        ${designTokens.media.tablet} {
          .screen-description {
            max-width: 600px;
          }
          
          .screen-description-base {
            font-size: ${designTokens.typography.fontSize.lg};   /* 18px */
          }
          
          .screen-description-lg {
            font-size: ${designTokens.typography.fontSize.xl};   /* 20px */
          }
        }
        
        /* Desktop (1024px+) - Industry standard body text */
        ${designTokens.media.desktop} {
          .screen-description {
            max-width: 700px;
          }
          
          .screen-description-base {
            font-size: ${designTokens.typography.fontSize.lg};   /* 18px for desktop body */
          }
          
          .screen-description-lg {
            font-size: ${designTokens.typography.fontSize.xl};   /* 20px */
          }
          
          .screen-description-xl {
            font-size: ${designTokens.typography.fontSize['2xl']}; /* 24px max */
          }
        }
        
        
        /* Desktop Enhanced mode now follows standard sizing */
        .screen-description.desktop-enhanced {
          /* Uses the same responsive sizes as defined above */
          /* This prevents over-scaling while maintaining the enhancement flag for compatibility */
        }
      `}</style>
    </>
  );
});
